import Page from '../../../shared/components/Page/Page';
import {Card, Col, Row, DatePicker, Radio, Table} from "antd";
import {useContext, useEffect, useMemo, useState} from "react";
import LoadingContext from "../../../app/contexts/LoadingContext";
import { getStatistic } from "../api";
import dayjs from 'dayjs';
import 'dayjs/locale/vi';
import styles from './Dashboard.module.scss';

const { RangePicker } = DatePicker;

function Dashboard() {
    const {setLoading} = useContext(LoadingContext);
    const [dataTable, setDataTable] = useState([]);
    const [dateRange, setDateRange] = useState([
        dayjs().subtract(6, 'day').startOf('day'),
        dayjs().endOf('day')
    ]);
    const [quickSelect, setQuickSelect] = useState('7days');

    const money = (value) => Number(value || 0).toLocaleString('vi-VN', { style: 'currency', currency: 'VND' });

    const totals = useMemo(() => dataTable.reduce(
        (acc, curr) => ({
            depositCount: acc.depositCount + Number(curr.depositCount || 0),
            depositAmount: acc.depositAmount + Number(curr.depositAmount || 0),
            withdrawCount: acc.withdrawCount + Number(curr.withdrawCount || 0),
            withdrawAmount: acc.withdrawAmount + Number(curr.withdrawAmount || 0),
            depositFee: acc.depositFee + Number(curr.depositFee || 0),
            withdrawFee: acc.withdrawFee + Number(curr.withdrawFee || 0),
            profit: acc.profit + Number(curr.profit || 0)
        }),
        { depositCount: 0, depositAmount: 0, withdrawCount: 0, withdrawAmount: 0, depositFee: 0, withdrawFee: 0, profit: 0 }
    ), [dataTable]);

    const metrics = [
        { key: 'deposit', label: 'Tiền nạp', value: money(totals.depositAmount) },
        { key: 'withdraw', label: 'Tiền rút', value: money(totals.withdrawAmount) },
        { key: 'fee', label: 'Phí', value: money(totals.depositFee + totals.withdrawFee) },
        { key: 'profit', label: 'Lợi nhuận', value: money(totals.profit) },
    ];

    const columns = [
        {
            title: 'Ngày',
            dataIndex: 'date',
            key: 'date',
            render: (text) => dayjs(text).format('DD/MM/YYYY')
        },
        {
            title: 'Số đơn nạp',
            dataIndex: 'depositCount',
            key: 'depositCount',
            align: 'right',
        },
        {
            title: 'Tiền nạp',
            dataIndex: 'depositAmount',
            key: 'depositAmount',
            align: 'right',
            render: (value) => value?.toLocaleString('vi-VN', { style: 'currency', currency: 'VND' })
        },
        {
            title: 'Số đơn rút',
            dataIndex: 'withdrawCount',
            key: 'withdrawCount',
            align: 'right',
        },
        {
            title: 'Tiền rút',
            dataIndex: 'withdrawAmount',
            key: 'withdrawAmount',
            align: 'right',
            render: (value) => value?.toLocaleString('vi-VN', { style: 'currency', currency: 'VND' })
        },
        {
            title: 'Phí nạp',
            dataIndex: 'depositFee',
            key: 'depositFee',
            align: 'right',
            render: (value) => value?.toLocaleString('vi-VN', { style: 'currency', currency: 'VND' })
        },
        {
            title: 'Phí rút',
            dataIndex: 'withdrawFee',
            key: 'withdrawFee',
            align: 'right',
            render: (value) => value?.toLocaleString('vi-VN', { style: 'currency', currency: 'VND' })
        },
        {
            title: 'Lợi nhuận',
            dataIndex: 'profit',
            key: 'profit',
            align: 'right',
            render: (value) => value?.toLocaleString('vi-VN', { style: 'currency', currency: 'VND' })
        }
    ];

    const fetchStatistic = async (startDate, endDate) => {
        setLoading(true);
        const response = await getStatistic( {
            startDate: startDate.format('YYYY-MM-DD'),
            endDate: endDate.format('YYYY-MM-DD')
        });
        if (response.success) {
            // Transform data from object format to array format for table
            const tableData = Object.entries(response.data).map(([date, item]) => ({
                key: date,
                date: date,
                depositCount: item.depositCount || 0,
                depositAmount: parseFloat(item.depositAmount) || 0,
                withdrawCount: item.withdrawCount || 0,
                withdrawAmount: parseFloat(item.withdrawAmount) || 0,
                depositFee: item.depositFee || 0,
                withdrawFee: item.withdrawFee || 0,
                profit: item.profit || 0
            }));
            setDataTable(tableData);
        }
        setLoading(false);
    }

    useEffect(() => {
        if (dateRange) {
            fetchStatistic(dateRange[0], dateRange[1]).finally();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dateRange]);

    const handleQuickSelectChange = (e) => {
        const value = e.target.value;
        setQuickSelect(value);
        
        let start, end;
        switch (value) {
            case '7days':
                start = dayjs().subtract(6, 'day').startOf('day');
                end = dayjs().endOf('day');
                break;
            case 'thisWeek':
                start = dayjs().startOf('week');
                end = dayjs().endOf('day');
                break;
            case 'lastWeek':
                start = dayjs().subtract(1, 'week').startOf('week');
                end = dayjs().subtract(1, 'week').endOf('week');
                break;
            default:
                return;
        }
        setDateRange([start, end]);
    };

    const handleRangePickerChange = (dates) => {
        setQuickSelect(null);
        setDateRange(dates);
    };

    return (
        <Page title="Trang chủ">
            <Row gutter={[16, 16]}>
                {metrics.map((item) => (
                    <Col xs={24} sm={12} lg={6} key={item.key}>
                        <div className={styles.metric}>
                            <div className={styles.label}>{item.label}</div>
                            <div className={styles.value}>{item.value}</div>
                        </div>
                    </Col>
                ))}
                <Col span={24}>
                    <Card
                        title={'Thống kê tổng quan'}
                        bordered={true}
                        className={styles.card}
                    >
                        <div className={styles.toolbar}>
                            <Radio.Group value={quickSelect} onChange={handleQuickSelectChange}>
                                <Radio.Button value="7days">7 ngày qua</Radio.Button>
                                <Radio.Button value="thisWeek">Tuần này</Radio.Button>
                                <Radio.Button value="lastWeek">Tuần trước</Radio.Button>
                            </Radio.Group>
                            <RangePicker
                                value={dateRange}
                                onChange={handleRangePickerChange}
                                format="DD/MM/YYYY"
                            />
                        </div>
                        <Table 
                            columns={columns} 
                            dataSource={dataTable}
                            pagination={false}
                            scroll={{ x: 'max-content' }}
                            className={styles.table}
                            summary={() => {
                                return (
                                    <Table.Summary fixed>
                                        <Table.Summary.Row>
                                            <Table.Summary.Cell index={0}>Tổng cộng</Table.Summary.Cell>
                                            <Table.Summary.Cell index={1} align="right">
                                                {totals.depositCount}
                                            </Table.Summary.Cell>
                                            <Table.Summary.Cell index={2} align="right">
                                                {totals.depositAmount.toLocaleString('vi-VN', { style: 'currency', currency: 'VND' })}
                                            </Table.Summary.Cell>
                                            <Table.Summary.Cell index={3} align="right">
                                                {totals.withdrawCount}
                                            </Table.Summary.Cell>
                                            <Table.Summary.Cell index={4} align="right">
                                                {totals.withdrawAmount.toLocaleString('vi-VN', { style: 'currency', currency: 'VND' })}
                                            </Table.Summary.Cell>
                                            <Table.Summary.Cell index={5} align="right">
                                                {totals.depositFee.toLocaleString('vi-VN', { style: 'currency', currency: 'VND' })}
                                            </Table.Summary.Cell>
                                            <Table.Summary.Cell index={6} align="right">
                                                {totals.withdrawFee.toLocaleString('vi-VN', { style: 'currency', currency: 'VND' })}
                                            </Table.Summary.Cell>
                                            <Table.Summary.Cell index={7} align="right">
                                                {totals.profit.toLocaleString('vi-VN', { style: 'currency', currency: 'VND' })}
                                            </Table.Summary.Cell>
                                        </Table.Summary.Row>
                                    </Table.Summary>
                                );
                            }}
                        />
                    </Card>
                </Col>
            </Row>
        </Page>
    );
}

export default Dashboard;
